import { useMemo } from "react";
import {
  AlertTriangle,
  CheckCircle,
  GraduationCap,
  HelpCircle,
  Link as LinkIcon,
  Lightbulb,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { CoachError } from "@/hooks/use-coach";

export interface CoachFeedbackCardProps {
  feedback: string;
  roundNumber: number;
  isStreaming: boolean;
  error: CoachError | null;
  onRetry?: () => void;
}

interface SectionKey {
  key: "strengths" | "refine" | "questions" | "alignment";
  title: string;
  aliases: readonly string[];
}

const SECTIONS: readonly SectionKey[] = [
  {
    key: "strengths",
    title: "Strengths",
    aliases: ["strengths", "strength"],
  },
  {
    key: "refine",
    title: "Areas to Refine",
    aliases: ["areas to refine", "refine", "areas for improvement"],
  },
  {
    key: "questions",
    title: "Questions to Consider",
    aliases: ["questions to consider", "questions"],
  },
  {
    key: "alignment",
    title: "Alignment Check",
    aliases: ["alignment check", "alignment"],
  },
];

const SECTION_STYLES: Record<SectionKey["key"], string> = {
  strengths:
    "bg-emerald-50 border-emerald-200 text-emerald-950 [&_.section-title]:text-emerald-800",
  refine:
    "bg-amber-50 border-amber-200 text-amber-950 [&_.section-title]:text-amber-800",
  questions:
    "bg-sky-50 border-sky-200 text-sky-950 [&_.section-title]:text-sky-800",
  alignment:
    "bg-[hsl(var(--muted))] border-[hsl(var(--border))] text-foreground [&_.section-title]:text-[hsl(var(--muted-foreground))]",
};

const SECTION_ICONS: Record<SectionKey["key"], typeof CheckCircle> = {
  strengths: CheckCircle,
  refine: AlertTriangle,
  questions: HelpCircle,
  alignment: LinkIcon,
};

export function CoachFeedbackCard({
  feedback,
  roundNumber,
  isStreaming,
  error,
  onRetry,
}: CoachFeedbackCardProps) {
  const parsed = useMemo(() => parseFeedback(feedback), [feedback]);
  const hasAnySection = parsed.some((s) => s.body.trim().length > 0);
  const showUnstructuredFallback =
    !hasAnySection && !isStreaming && feedback.trim().length > 0;

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center justify-between bg-[hsl(var(--primary))] px-4 py-3 text-[hsl(var(--primary-foreground))]">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5" />
          <span className="font-heading text-base font-semibold">OKR Coach</span>
          {isStreaming && (
            <span
              aria-label="Coaching in progress"
              className="ml-2 inline-block h-2 w-2 animate-pulse rounded-full bg-[hsl(var(--accent))]"
            />
          )}
        </div>
        <span className="rounded-full bg-[hsl(var(--accent))] px-2.5 py-0.5 text-xs font-semibold text-[hsl(var(--accent-foreground))]">
          Round {roundNumber}
        </span>
      </div>

      <div className="space-y-3 p-4">
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">
            <p className="text-destructive">{error.message}</p>
            {feedback.length > 0 && (
              <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                Partial feedback is preserved below.
              </p>
            )}
            {onRetry && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={onRetry}
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Try Again
              </Button>
            )}
          </div>
        )}

        {!hasAnySection && !error && isStreaming && (
          <p className="text-sm italic text-[hsl(var(--muted-foreground))]">
            Thinking...
          </p>
        )}

        {showUnstructuredFallback && (
          <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3 text-foreground">
            <FeedbackBody text={feedback} />
          </div>
        )}

        {parsed.map((section, idx) => {
          if (!section.body.trim()) return null;
          const Icon = SECTION_ICONS[section.key];
          return (
            <div
              key={section.key}
              className={cn(
                "rounded-md border p-3 animate-in fade-in slide-in-from-bottom-1 duration-300 fill-mode-both",
                SECTION_STYLES[section.key],
              )}
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className="section-title mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
                <Icon className="h-3.5 w-3.5" />
                {section.title}
              </div>
              <FeedbackBody text={section.body} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

interface ParsedSection {
  key: SectionKey["key"];
  title: string;
  body: string;
}

const SENTINEL_LINE_RE =
  /^\*{0,2}(?:READINESS|RETURN_TO_OBJECTIVE|MODE_RECALIBRATED)\*{0,2}:/i;

function parseFeedback(raw: string): ParsedSection[] {
  const result: ParsedSection[] = SECTIONS.map((s) => ({
    key: s.key,
    title: s.title,
    body: "",
  }));
  if (!raw.trim()) return result;

  const lines = raw.split(/\r?\n/);
  let currentIdx = -1;
  const buckets: string[][] = SECTIONS.map(() => []);

  for (const line of lines) {
    const matched = matchSectionHeading(line);
    if (matched !== -1) {
      currentIdx = matched;
      continue;
    }
    if (currentIdx !== -1) {
      const trimmed = line.trim();
      if (trimmed === "---" || SENTINEL_LINE_RE.test(trimmed)) continue;
      buckets[currentIdx]!.push(line);
    }
  }

  for (let i = 0; i < SECTIONS.length; i++) {
    result[i]!.body = (buckets[i] ?? []).join("\n").trim();
  }
  return result;
}

function matchSectionHeading(line: string): number {
  const stripped = line
    .replace(/^#+\s*/, "")
    .replace(/\*\*/g, "")
    .replace(/[:：].*$/, "")
    .trim()
    .toLowerCase();
  if (!stripped) return -1;
  for (let i = 0; i < SECTIONS.length; i++) {
    if (SECTIONS[i]!.aliases.includes(stripped)) return i;
  }
  return -1;
}

/**
 * Renders section body as paragraphs + bullet lists.
 * Text is plain rendered via React (no dangerouslySetInnerHTML) — safe from XSS.
 */
function FeedbackBody({ text }: { text: string }) {
  const blocks = useMemo(() => splitBlocks(text), [text]);
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {blocks.map((block, i) =>
        block.kind === "list" ? (
          <ul key={i} className="list-disc space-y-1 pl-5">
            {block.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{block.text}</p>
        ),
      )}
    </div>
  );
}

type Block = { kind: "para"; text: string } | { kind: "list"; items: string[] };

function stripBold(text: string): string {
  return text.replace(/\*\*/g, "");
}

function splitBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.split(/\r?\n/);
  let paraBuf: string[] = [];
  let listBuf: string[] = [];

  const flushPara = () => {
    if (paraBuf.length) {
      blocks.push({ kind: "para", text: paraBuf.join(" ").trim() });
      paraBuf = [];
    }
  };
  const flushList = () => {
    if (listBuf.length) {
      blocks.push({ kind: "list", items: listBuf });
      listBuf = [];
    }
  };

  for (const line of lines) {
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    if (bullet) {
      flushPara();
      listBuf.push(stripBold(bullet[1]!));
      continue;
    }
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    flushList();
    paraBuf.push(stripBold(line.trim()));
  }
  flushPara();
  flushList();
  return blocks;
}

export function CoachFeedbackPlaceholder() {
  return (
    <Card className="flex min-h-[320px] items-center justify-center p-8 text-center">
      <div className="space-y-3">
        <Lightbulb className="mx-auto h-12 w-12 text-[hsl(var(--muted-foreground))]" />
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Write your objective and click{" "}
          <span className="font-semibold text-foreground">
            Get Coaching Feedback
          </span>{" "}
          to receive personalized guidance.
        </p>
      </div>
    </Card>
  );
}
